import { queryOptions, useMutation } from "@tanstack/react-query";

import { sortString } from "@/common/utils/array";
import { getDateFromISOString } from "@/common/utils/date";
import {
  assignCompanyUser,
  CompanyUserAssignRequest,
  createManual,
  CustomPriceRequest,
  deleteCustomPrice,
  getCompany,
  getCompanyUsers,
  getCountryPlansIncludingManual,
  setCustomPrice,
  SubscriptionCreateManualRequest,
} from "@/generated/api/users";

const selectCompany = (data: Awaited<ReturnType<typeof getCompany>>) => ({
  ...data,
  subscriptions: data.subscriptions.map((s) => ({
    ...s,
    createdAt: getDateFromISOString(s.createdAt),
    expiresAt: getDateFromISOString(s.expiresAt),
  })),
});

const companyQuery = ({ id }: { id: number }) =>
  queryOptions({
    queryFn: ({ signal }) => getCompany(id, { signal }),
    queryKey: ["company", id],
    select: selectCompany,
  });

const companyUsersQuery = ({ id }: { id: number }) =>
  queryOptions({
    queryFn: ({ signal }) => getCompanyUsers(id, undefined, { signal }),
    queryKey: ["company", id, "users"],
    select: (users) =>
      users.toSorted((a, b) =>
        sortString(a.userEmail ?? "", b.userEmail ?? ""),
      ),
  });

const countryPlansIncludingManualQuery = ({
  countryId,
}: {
  countryId: string;
}) =>
  queryOptions({
    queryFn: ({ signal }) =>
      getCountryPlansIncludingManual(countryId, undefined, { signal }),
    queryKey: ["country", countryId, "plans", "including-manual"],
  });

const useUpdateCompanySubscriptionPlanMutation = () =>
  useMutation({
    mutationFn: (payload: SubscriptionCreateManualRequest) =>
      createManual({
        companyId: payload.companyId,
        expiresAt: payload.expiresAt,
        plan: payload.plan,
      }),
  });

const useAssignCompanyUserMutation = () =>
  useMutation({
    mutationFn: (payload: {
      companyId: number;
      companyUserAssignRequest: CompanyUserAssignRequest;
    }) =>
      assignCompanyUser(payload.companyId, payload.companyUserAssignRequest),
  });

const useSetCustomPriceMutation = () =>
  useMutation({
    mutationFn: (payload: { companyId: number; request: CustomPriceRequest }) =>
      setCustomPrice(payload.companyId, payload.request),
  });

const useDeleteCustomPriceMutation = () =>
  useMutation({
    mutationFn: (payload: { companyId: number; planPeriodId: number }) =>
      deleteCustomPrice(payload.companyId, payload.planPeriodId),
  });

type CompanyUser = Awaited<ReturnType<typeof getCompanyUsers>>[number];

export {
  companyQuery,
  companyUsersQuery,
  countryPlansIncludingManualQuery,
  useAssignCompanyUserMutation,
  useDeleteCustomPriceMutation,
  useSetCustomPriceMutation,
  useUpdateCompanySubscriptionPlanMutation,
};
export type { CompanyUser };
